/**
 * /erp/raw-materials — list page (Manager+ via the /erp shell gate).
 *
 * Mirrors the products list page in shape; raw materials don't have a
 * default sell price column (they're inputs, not outputs) but DO show
 * a small badge when the row is mirrored from a product (is read-only
 * direct edits — the manager edits via the product).
 */

import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Plus, Search, Wrench } from "lucide-react";
import { useMemo, useState } from "react";

import { ImportDialog } from "../components/ImportDialog";
import { useAuthStore } from "../lib/auth";
import { ERP_QUERY_KEYS, useProducts, useRawMaterials } from "../lib/erp";

export const Route = createFileRoute("/_app/erp/raw-materials/")({
  component: RawMaterialsListPage,
});

function RawMaterialsListPage() {
  const queryClient = useQueryClient();
  const role = useAuthStore((state) => state.user?.role);
  const isProduction = role === "production";
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const rawsQ = useRawMaterials();
  const productsQ = useProducts();

  const mirroredRawIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of productsQ.data?.items ?? []) {
      if (p.linked_raw_material_id) set.add(p.linked_raw_material_id);
    }
    return set;
  }, [productsQ.data]);

  const raws = rawsQ.data?.items ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return raws.filter((r) => {
      if (!showInactive && !r.is_active) return false;
      if (!needle) return true;
      return r.name.toLowerCase().includes(needle) || r.code.toLowerCase().includes(needle);
    });
  }, [raws, q, showInactive]);

  return (
    <div className="page-shell space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Wrench className="size-5 opacity-70" aria-hidden />
            Raw Materials
          </h1>
          <p className="text-sm opacity-60">
            Consumable inputs for manufacturing. Codes auto-generate as RAW-NNNNN.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(role === "admin" || role === "manager") && (
            <ImportDialog
            entityLabel="Raw Materials"
            importPath="/api/erp/raw-materials/import"
            templatePath="/api/erp/raw-materials/import/template"
            columns={["code", "name", "uom", "notes"]}
            onImported={() =>
              queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterials })
            }
            />
          )}
          <Link
            to="/erp/raw-materials/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-3.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden /> New Raw Material
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name or code…"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-1.5 pl-8 pr-3 text-sm outline-none focus:border-[var(--accent)]"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs opacity-80">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="size-3.5"
          />
          Show inactive
        </label>
        <span className="text-xs opacity-60">
          {filtered.length} {filtered.length === 1 ? "row" : "rows"}
        </span>
      </div>

      {rawsQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : rawsQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load raw materials: {(rawsQ.error as Error).message}
        </p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <Wrench className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
          <p className="text-sm font-medium">
            {raws.length === 0 ? "No raw materials yet." : "No raw materials match."}
          </p>
          <p className="mt-1 text-sm opacity-60">
            {raws.length === 0
              ? "Create one to start building BOMs."
              : "Try a different search or include inactive rows."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
          <table className="w-full min-w-[42rem] text-sm">
            <thead className="bg-black/5 text-xs uppercase tracking-wider opacity-70 dark:bg-white/5">
              <tr>
                <th className="px-4 py-3.5 text-left">Code</th>
                <th className="px-4 py-3.5 text-left">Name</th>
                <th className="px-4 py-3.5 text-left">UoM</th>
                {!isProduction && <th className="hidden px-4 py-3.5 text-left md:table-cell">Mirrored</th>}
                <th className="px-4 py-3.5 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3.5">
                    <Link
                      to="/erp/raw-materials/$rawMaterialId"
                      params={{ rawMaterialId: r.id }}
                      className="font-mono text-xs font-medium hover:underline"
                    >
                      {r.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3.5">
                    <Link
                      to="/erp/raw-materials/$rawMaterialId"
                      params={{ rawMaterialId: r.id }}
                      className="hover:underline"
                    >
                      {r.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3.5 text-xs opacity-80">{r.uom}</td>
                  {!isProduction && (
                    <td className="hidden px-4 py-3.5 text-xs opacity-60 md:table-cell">
                      {mirroredRawIds.has(r.id) ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          From product
                        </span>
                      ) : "—"}
                    </td>
                  )}
                  <td className="px-4 py-3.5">
                    <StatusPill active={r.is_active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
        active
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : "bg-black/10 opacity-60 dark:bg-white/10"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}
